import { useCallback, useEffect, useRef, useState } from 'react';
import { CopilotWsClient, CopilotWsMessage } from './copilotWsClient';

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  isProgress?: boolean;
}

interface UseCopilotChatArgs {
  endpoint: string;
  caseId: string;
}

export interface UseCopilotChatReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isSending: boolean;
  error: string | null;
  conversationId: string | undefined;
  sendMessage: (text: string) => Promise<void>;
}

const STREAM_FLUSH_MS = 50;

export function useCopilotChat({
  endpoint,
  caseId,
}: UseCopilotChatArgs): UseCopilotChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>();

  const clientRef = useRef<CopilotWsClient | null>(null);
  const streamBufferRef = useRef<string>('');
  const streamFlushRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (!clientRef.current) {
    clientRef.current = new CopilotWsClient(endpoint);
  }

  const flushStream = useCallback(() => {
    if (!streamBufferRef.current) return;
    const chunk = streamBufferRef.current;
    streamBufferRef.current = '';
    streamFlushRef.current = null;

    setMessages((prev) => {
      const existingIdx = prev.findIndex((m) => m.id === 'streaming');
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = {
          ...next[existingIdx],
          text: next[existingIdx].text + chunk,
        };
        return next;
      }
      const withoutProgress = prev.filter((m) => !m.isProgress);
      return [
        ...withoutProgress,
        {
          id: 'streaming',
          text: chunk,
          sender: 'assistant',
          timestamp: new Date(),
        },
      ];
    });
  }, []);

  const handleMessage = useCallback(
    (data: CopilotWsMessage) => {
      if (data.type === 'chat_response') {
        if (streamFlushRef.current) {
          clearTimeout(streamFlushRef.current);
          streamFlushRef.current = null;
        }
        streamBufferRef.current = '';

        const convId = data.conversationId as string | undefined;
        if (convId) setConversationId(convId);

        const newMsg: ChatMessage = {
          id:
            (data.id as string | undefined) ||
            `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          text:
            (data.message as string | undefined) ||
            (data.text as string | undefined) ||
            '',
          sender: 'assistant',
          timestamp: new Date(
            (data.timestamp as number | undefined) || Date.now()
          ),
        };

        setIsSending(false);
        setMessages((prev) => [
          ...prev.filter((m) => !m.isProgress && m.id !== 'streaming'),
          newMsg,
        ]);
      } else if (
        data.type === 'progress' ||
        data.type === 'in_progress' ||
        data.type === 'acknowledgment' ||
        data.type === 'thought_update'
      ) {
        const text =
          (data.message as string | undefined) ||
          (data.text as string | undefined) ||
          (data.thought as string | undefined) ||
          'Thinking...';
        setMessages((prev) => {
          const withoutProgress = prev.filter((m) => !m.isProgress);
          return [
            ...withoutProgress,
            {
              id: 'progress',
              text,
              sender: 'assistant',
              timestamp: new Date(),
              isProgress: true,
            },
          ];
        });
      } else if (data.type === 'stream_chunk') {
        const convId = data.conversationId as string | undefined;
        if (convId) setConversationId(convId);
        streamBufferRef.current += (data.chunk as string | undefined) || '';
        if (!streamFlushRef.current) {
          streamFlushRef.current = setTimeout(flushStream, STREAM_FLUSH_MS);
        }
      } else if (data.type === 'error') {
        setError((data.message as string | undefined) || 'Chat error');
        setIsSending(false);
        setMessages((prev) => prev.filter((m) => !m.isProgress));
      }
    },
    [flushStream]
  );

  useEffect(() => {
    const client = clientRef.current!;
    const offMsg = client.onMessage(handleMessage);
    const offStatus = client.onStatusChange((connected) => {
      setIsConnected(connected);
    });
    return () => {
      offMsg();
      offStatus();
      if (streamFlushRef.current) clearTimeout(streamFlushRef.current);
      client.disconnect();
    };
  }, [handleMessage]);

  const sendMessage = useCallback(
    async (text: string) => {
      const client = clientRef.current!;
      const trimmed = text.trim();
      if (!trimmed) return;

      setError(null);
      setIsSending(true);

      const userMsg: ChatMessage = {
        id: `user_${Date.now()}`,
        text: trimmed,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);

      try {
        let convId = conversationId;
        if (!convId) {
          convId =
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `conv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
          setConversationId(convId);
        }
        if (!client.isConnected()) {
          await client.connect({ conversationId: convId, caseId });
          await new Promise<void>((resolve, reject) => {
            const start = Date.now();
            const check = () => {
              if (client.isConnected()) return resolve();
              if (Date.now() - start > 10000) {
                return reject(new Error('Timed out connecting to copilot'));
              }
              setTimeout(check, 100);
            };
            check();
          });
        }
        client.sendMessage(trimmed, convId, []);
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err.message : 'Failed to send');
        setIsSending(false);
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      }
    },
    [conversationId, caseId]
  );

  return {
    messages,
    isConnected,
    isSending,
    error,
    conversationId,
    sendMessage,
  };
}
